import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import { useCurrency } from '@/hooks/useCurrency';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import CurrencyInput from '@/components/ui/CurrencyInput';

const QUICK_AMOUNTS = [25, 50, 100, 250, 500];

export default function AddFundsModal({ open, onClose, goal }) {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const addFunds = useStore((s) => s.addFundsToGoal);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  if (!goal) return null;

  const remaining = goal.target - goal.saved;

  const handleSave = () => {
    const val = Number(amount);
    if (!val || val <= 0) {
      setError(t('goals.form.amountRequired'));
      return;
    }
    addFunds(goal.id, val);
    handleClose();
  };

  const handleClose = () => {
    setAmount('');
    setError('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={t('goals.addFunds')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('goals.addFunds')}
      </Button>
    }>
      <div className="space-y-4">
        <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <span className="text-3xl">{goal.icon}</span>
          <div className="font-semibold text-gray-900 dark:text-white mt-2">{goal.name}</div>
          <div className="text-sm text-gray-500">
            {t('goals.form.remaining')}: {formatCurrency(remaining)}
          </div>
        </div>

        {/* Quick amounts */}
        <div className="flex flex-wrap gap-2">
          {QUICK_AMOUNTS.filter((a) => a <= remaining).map((a) => (
            <button
              key={a}
              onClick={() => setAmount(String(a))}
              className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                amount === String(a)
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
              }`}
            >
              {formatCurrency(a)}
            </button>
          ))}
        </div>

        {/* Custom amount */}
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-300">$</span>
          <CurrencyInput
            placeholder="0"
            value={amount}
            onChange={setAmount}
            className={`w-full pl-10 pr-4 py-3 text-lg font-bold input ${error ? 'border-danger-500' : ''}`}
          />
        </div>
        {error && <p className="text-xs text-danger-500">{error}</p>}

      </div>
    </Modal>
  );
}
